import React, { ReactElement, useEffect, useState } from 'react';

import Icon from 'atomic/icon/icon';
import Button from 'atomic/button/button';
import { useFetch } from 'hooks/use-fetch';
import LocalStorageQueue from 'helpers/local-storage-queue';

import './review-item-likes.scss';

interface ReviewItemLikesProps {
    reviewId: number;
    likes?: number;
    dislikes?: number;
    type: 'company' | 'product';
}

enum likesLocalStorageKeys {
    likes = 'likes',
    dislikes = 'dislikes'
}

const ReviewItemLikes: React.FC<ReviewItemLikesProps> = ({ reviewId, likes, dislikes, type }): ReactElement => {
    const [likesCount, setLikesCount] = useState(likes || 0);
    const [dislikesCount, setDislikesCount] = useState(dislikes || 0);
    const [likeDisabled, setLikeDisabled] = useState(false);
    const [dislikeDisabled, setDislikeDisabled] = useState(false);

    const likeFetch = useFetch(`/${type}-review/like/${reviewId}`);
    const dislikeFetch = useFetch(`/${type}-review/dislike/${reviewId}`);

    useEffect(() => {
        const likedOrUnliked = LocalStorageQueue.hasItem(likesLocalStorageKeys.likes, String(reviewId))
            || LocalStorageQueue.hasItem(likesLocalStorageKeys.dislikes, String(reviewId));

        setLikeDisabled(likedOrUnliked);
        setDislikeDisabled(likedOrUnliked);
    }, [reviewId]);

    const like = (): void => {
        setLikeDisabled(true);
        setDislikeDisabled(true);

        likeFetch.get()
            .then((response: boolean) => {
                if (response) {
                    setLikesCount(prev => prev + 1);
                    LocalStorageQueue.addItem(likesLocalStorageKeys.likes, String(reviewId));
                } else {
                    setLikeDisabled(false);
                    setDislikeDisabled(false);
                }
            })
            .catch(() => {
                setLikeDisabled(false);
                setDislikeDisabled(false);
            });
    };

    const dislike = (): void => {
        setDislikeDisabled(true);
        setLikeDisabled(true);

        dislikeFetch.get()
            .then((response: boolean) => {
                if (response) {
                    setDislikesCount(prev => prev + 1);
                    LocalStorageQueue.addItem(likesLocalStorageKeys.dislikes, String(reviewId));
                } else {
                    setDislikeDisabled(false);
                    setLikeDisabled(false);
                }
            })
            .catch(() => {
                setDislikeDisabled(false);
                setLikeDisabled(false);
            });
    };

    return (
        <span>
            <Button wrapper={true} onClick={like} disabled={likeFetch.loading || likeDisabled}
                className="review-item__like-button"
            >
                <Icon icon="like" className="review-item__like-icon"/>
                {` ${likesCount} `}
            </Button>
            {' '}
            <Button wrapper={true} onClick={dislike} disabled={dislikeFetch.loading || dislikeDisabled}
                className="review-item__like-button"
            >
                <Icon icon="dislike" className="review-item__like-icon"/>
                {` ${dislikesCount} `}
            </Button>
        </span>
    );
};

export default ReviewItemLikes;
